import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '@config/firebase';

const INTERNAL_ADMIN_EMAIL = 'internal-admin@latteconnect.com';

interface InternalAdminContextType {
  currentUser: User | null;
  isInternalAdmin: boolean;
  loading: boolean;
}

const InternalAdminContext = createContext<InternalAdminContextType | undefined>(undefined);

export const useInternalAdmin = () => {
  const context = useContext(InternalAdminContext);
  if (context === undefined) {
    throw new Error('useInternalAdmin must be used within an InternalAdminProvider');
  }
  return context;
};

interface InternalAdminProviderProps {
  children: React.ReactNode;
}

export const InternalAdminProvider: React.FC<InternalAdminProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isInternalAdmin, setIsInternalAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Check both email and admin custom claim
        const hasAdminEmail = user.email === INTERNAL_ADMIN_EMAIL;

        // Get ID token result to access custom claims
        try {
          const tokenResult = await user.getIdTokenResult();
          const hasAdminClaim = tokenResult.claims.admin === true;
          setIsInternalAdmin(hasAdminEmail && hasAdminClaim);
        } catch (error) {
          console.error('Error getting token claims:', error);
          setIsInternalAdmin(false);
        }
      } else {
        setIsInternalAdmin(false);
      }

      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  const value: InternalAdminContextType = {
    currentUser,
    isInternalAdmin,
    loading,
  };

  return (
    <InternalAdminContext.Provider value={value}>
      {children}
    </InternalAdminContext.Provider>
  );
};
